import {
  getRacesForRuleset,
  getClassesForRuleset,
  getBackgroundsForRuleset,
  getFeats,
  getFightingStylesForClass,
  getFightingStyleLevel,
  getASILevels,
  meetsFeatPrerequisites,
  // Phase 3: Class features
  getInvocationCount,
  getAvailableInvocations,
  METAMAGIC_OPTIONS,
  getMetamagicCount,
  FAVORED_ENEMIES,
  getFavoredEnemyCount,
  NATURAL_TERRAINS,
  getNaturalExplorerCount,
  PACT_BOONS,
  // Weapon utilities for attack generation
  findWeaponByName,
  getWeaponAbility,
  formatAttackBonus,
} from '@character-creator/data';
import type { AbilityScores, AbilityName, Alignment, SkillName, CharacterClass, Background, FeatDefinition } from '@character-creator/types';
import { calculateModifier, calculateProficiencyBonus, applyAsiIncreases, applyMultipleAsiIncreases, meetsMulticlassPrerequisites } from '@character-creator/core';
import type { CharacterDraft, AsiChoice } from '../stores/characterCreationStore';
import { getRandomEquipmentSelections, getRandomSpellSelection } from './characterDefaults';

// Fantasy first name components
const FIRST_NAME_PREFIXES = [
  'Ael', 'Aer', 'Al', 'An', 'Ar', 'As', 'Bal', 'Bel', 'Br', 'Car',
  'Cor', 'Cyr', 'Dal', 'Del', 'Dor', 'El', 'Er', 'Eth', 'Fal', 'Fen',
  'Gal', 'Gar', 'Gil', 'Gor', 'Hal', 'Hel', 'Il', 'Ir', 'Jor', 'Kal',
  'Kar', 'Kel', 'Kir', 'Kor', 'Lae', 'Lar', 'Lor', 'Lyr', 'Mal', 'Mar',
  'Mor', 'Nal', 'Nor', 'Or', 'Ral', 'Ren', 'Ril', 'Sal', 'Sar', 'Sel',
  'Sil', 'Sol', 'Tal', 'Tar', 'Tel', 'Thal', 'Thor', 'Tor', 'Ul', 'Val',
  'Var', 'Vel', 'Vor', 'Wil', 'Xan', 'Yor', 'Zal', 'Zan', 'Zor',
];

const FIRST_NAME_SUFFIXES = [
  'a', 'ac', 'ad', 'ae', 'ak', 'al', 'am', 'an', 'ar', 'as', 'ax',
  'dan', 'dar', 'dor', 'dric', 'drin', 'e', 'ean', 'el', 'en', 'er',
  'eth', 'eus', 'ian', 'ias', 'iel', 'ien', 'ik', 'il', 'im', 'in',
  'ion', 'ir', 'is', 'ith', 'ius', 'o', 'od', 'ok', 'ol', 'om', 'on',
  'or', 'oth', 'ra', 'rak', 'ran', 'ren', 'ric', 'rik', 'rin', 'ris',
  'ron', 'ros', 'th', 'thas', 'thon', 'tor', 'ul', 'um', 'un', 'ur',
  'us', 'yn', 'yth',
];

// Fantasy surname components
const SURNAME_PREFIXES = [
  'Black', 'Bright', 'Dark', 'Dawn', 'Dead', 'Deep', 'Dragon', 'Dusk',
  'Eagle', 'Earth', 'Ever', 'Fair', 'Falcon', 'Far', 'Fire', 'Frost',
  'Gold', 'Good', 'Gray', 'Green', 'Hawk', 'High', 'Hill', 'Ice',
  'Iron', 'Light', 'Lion', 'Long', 'Moon', 'Night', 'Oak', 'Raven',
  'Red', 'River', 'Rock', 'Rose', 'Shadow', 'Sharp', 'Silent', 'Silver',
  'Sky', 'Snow', 'Star', 'Steel', 'Stone', 'Storm', 'Strong', 'Summer',
  'Sun', 'Swift', 'Thorn', 'Thunder', 'True', 'War', 'White', 'Wild',
  'Wind', 'Winter', 'Wolf', 'Wood',
];

const SURNAME_SUFFIXES = [
  'arrow', 'axe', 'bane', 'basher', 'beard', 'blade', 'blood', 'bloom',
  'born', 'bough', 'brand', 'breaker', 'brew', 'bridge', 'brook', 'brow',
  'claw', 'cliff', 'crest', 'crown', 'dale', 'fall', 'fang', 'field',
  'fire', 'fist', 'flame', 'foot', 'forge', 'fury', 'gaze', 'guard',
  'hammer', 'hand', 'heart', 'helm', 'hold', 'hollow', 'horn', 'hunter',
  'keep', 'kind', 'leaf', 'mane', 'mantle', 'mark', 'maul', 'mist',
  'moon', 'more', 'rage', 'reign', 'rider', 'river', 'root', 'runner',
  'seeker', 'shade', 'shield', 'singer', 'slayer', 'song', 'soul', 'spear',
  'spell', 'spirit', 'stalker', 'star', 'stone', 'stride', 'striker', 'sun',
  'sword', 'vale', 'walker', 'ward', 'warden', 'water', 'weaver', 'wind',
  'wing', 'wood', 'worthy',
];

function pick<T>(arr: readonly T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

function roll(sides: number): number {
  return Math.floor(Math.random() * sides) + 1;
}

// Alignment options
const ALIGNMENTS: Alignment[] = [
  'lawful-good', 'neutral-good', 'chaotic-good',
  'lawful-neutral', 'true-neutral', 'chaotic-neutral',
  'lawful-evil', 'neutral-evil', 'chaotic-evil',
];

// Appearance generation data
const EYE_COLORS = ['brown', 'blue', 'green', 'hazel', 'gray', 'amber', 'black', 'violet'];
const HAIR_COLORS = ['black', 'brown', 'blonde', 'red', 'gray', 'white', 'auburn', 'silver'];
const SKIN_TONES = ['pale', 'fair', 'tan', 'olive', 'brown', 'dark', 'bronze', 'golden'];

// Backstory template components
const ORIGIN_PHRASES = [
  'grew up in humble circumstances',
  'was born into a family of modest means',
  'spent their early years wandering',
  'came from a small village',
  'was raised by distant relatives',
  'grew up on the outskirts of civilization',
];

const DISCOVERY_PHRASES = [
  'before discovering their calling as a',
  'until fate intervened and they became a',
  'before their life changed forever and they chose the path of a',
  'until they found their true path as a',
  'before destiny called them to become a',
];

const MOTIVATION_PHRASES = [
  'Now they seek adventure and purpose',
  'Today they wander in search of meaning',
  'Their journey has only just begun',
  'They now pursue their destiny with determination',
  'Adventure calls them ever onward',
];

/**
 * Generate a random alignment
 */
function generateAlignment(): Alignment {
  return pick(ALIGNMENTS);
}

/**
 * Generate random appearance details
 */
function generateAppearance(): { age?: string; height?: string; weight?: string; eyes?: string; skin?: string; hair?: string } {
  const heightInches = roll(12) + 58; // 59-70 inches (4'11" to 5'10")
  const feet = Math.floor(heightInches / 12);
  const inches = heightInches % 12;

  return {
    age: `${roll(40) + 18}`,
    height: `${feet}'${inches}"`,
    weight: `${roll(80) + 100} lbs`,
    eyes: pick(EYE_COLORS),
    hair: pick(HAIR_COLORS),
    skin: pick(SKIN_TONES),
  };
}

/**
 * Generate starting currency (standard 15 gp for most backgrounds)
 */
function generateCurrency(_background: Background): { cp: number; sp: number; ep: number; gp: number; pp: number } {
  // Most backgrounds provide 15 gp as starting currency (embedded in equipment string)
  return {
    cp: 0,
    sp: 0,
    ep: 0,
    gp: 15,
    pp: 0,
  };
}

/**
 * Generate a simple backstory based on character details
 */
function generateBackstory(
  name: string,
  className: string,
  backgroundName: string,
  ideal: string,
  bond: string,
): string {
  const origin = pick(ORIGIN_PHRASES);
  const discovery = pick(DISCOVERY_PHRASES);
  const motivation = pick(MOTIVATION_PHRASES);

  // Extract first sentence of ideal/bond for incorporation
  const idealShort = ideal.split('.')[0];
  const bondShort = bond.split('.')[0];

  return `${name} ${origin} ${discovery} ${className.toLowerCase()}. Their time as a ${backgroundName.toLowerCase()} shaped who they are today. When asked to describe their ideal, they say "${idealShort}. ${bondShort}." ${motivation}.`;
}

/**
 * Generate a random fantasy first name
 */
function generateFirstName(): string {
  return pick(FIRST_NAME_PREFIXES) + pick(FIRST_NAME_SUFFIXES);
}

/**
 * Generate a random fantasy surname
 */
function generateSurname(): string {
  return pick(SURNAME_PREFIXES) + pick(SURNAME_SUFFIXES).toLowerCase();
}

/**
 * Generate a random fantasy full name (Firstname Lastname)
 */
export function generateRandomName(): string {
  return `${generateFirstName()} ${generateSurname()}`;
}

/**
 * Roll 4d6 drop lowest for ability scores
 */
export function rollAbilityScore(): number {
  const rolls = [roll(6), roll(6), roll(6), roll(6)];
  rolls.sort((a, b) => b - a);
  return rolls[0] + rolls[1] + rolls[2]; // Drop lowest
}

/**
 * Generate random ability scores using 4d6 drop lowest
 */
export function generateRandomAbilityScores(): AbilityScores {
  return {
    strength: rollAbilityScore(),
    dexterity: rollAbilityScore(),
    constitution: rollAbilityScore(),
    intelligence: rollAbilityScore(),
    wisdom: rollAbilityScore(),
    charisma: rollAbilityScore(),
  };
}

/**
 * Shuffle an array in place (Fisher-Yates)
 */
function shuffle<T>(arr: T[]): T[] {
  const result = [...arr];
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

/**
 * Generate a random level from 1-10, weighted toward mid-levels (3-6)
 */
function generateLevel(targetLevel?: number): number {
  if (targetLevel !== undefined) {
    return Math.max(1, Math.min(10, targetLevel));
  }
  // Weighted distribution favoring levels 3-6
  const weights = [5, 8, 15, 18, 18, 15, 8, 5, 4, 4]; // levels 1-10
  const total = weights.reduce((a, b) => a + b, 0);
  let rollValue = Math.random() * total;
  for (let i = 0; i < weights.length; i++) {
    rollValue -= weights[i];
    if (rollValue <= 0) return i + 1;
  }
  return 5; // fallback
}

/**
 * Get subclass ID based on character level and class requirements
 */
function generateSubclass(
  characterClass: CharacterClass,
  level: number,
): string | null {
  if (level < characterClass.subclassLevel || !characterClass.subclasses?.length) {
    return null;
  }
  return pick(characterClass.subclasses).id;
}

/**
 * Generate a random ability increase (+2 to one or +1 to two)
 */
function generateAbilityIncrease(scores: AbilityScores): AsiChoice {
  const abilities = (Object.entries(scores)
    .filter(([_, v]) => v < 20)
    .map(([k]) => k)) as AbilityName[];

  // If no abilities can be increased, fallback
  if (abilities.length === 0) {
    return { type: 'ability-increase', increases: [{ ability: 'strength', amount: 0 }] };
  }

  if (Math.random() < 0.5 && abilities.length >= 2) {
    // +1 to two abilities
    const shuffled = shuffle(abilities);
    return {
      type: 'ability-increase',
      increases: [
        { ability: shuffled[0], amount: 1 },
        { ability: shuffled[1], amount: 1 },
      ],
    };
  } else {
    // +2 to one ability
    return {
      type: 'ability-increase',
      increases: [{ ability: pick(abilities), amount: 2 }],
    };
  }
}

/**
 * Generate ASI/Feat choices (70% ASI, 30% Feat)
 */
function generateAsiChoices(
  classId: string,
  level: number,
  abilityScores: AbilityScores,
  rulesetId: string,
  raceId?: string,
  hasSpellcasting?: boolean,
): Record<number, AsiChoice> {
  const asiLevels = getASILevels(classId);
  const choices: Record<number, AsiChoice> = {};

  // Get feats available for this ruleset
  const allFeats = getFeats(rulesetId);

  for (const asiLevel of asiLevels) {
    if (level < asiLevel) break;

    if (Math.random() < 0.7) {
      // 70% chance: Take ability score increase
      choices[asiLevel] = generateAbilityIncrease(abilityScores);
    } else {
      // 30% chance: Take a feat
      const eligibleFeats = allFeats.filter((feat: FeatDefinition) => {
        const result = meetsFeatPrerequisites(feat, {
          abilityScores: abilityScores as unknown as Record<string, number>,
          level: asiLevel,
          raceId,
          classId,
          hasSpellcasting,
        });
        return result.meets;
      });

      if (eligibleFeats.length > 0) {
        choices[asiLevel] = { type: 'feat', featId: pick(eligibleFeats).id };
      } else {
        // Fallback to ASI if no feats qualify
        choices[asiLevel] = generateAbilityIncrease(abilityScores);
      }
    }
  }

  return choices;
}

/**
 * Generate expertise for Rogue/Bard
 * - Rogue: levels 1, 6 (2 skills each)
 * - Bard: levels 3, 10 (2 skills each)
 */
function generateExpertise(
  classId: string,
  level: number,
  skillProficiencies: SkillName[],
): SkillName[] {
  const expertiseLevels = classId === 'rogue' ? [1, 6] : classId === 'bard' ? [3, 10] : [];
  const numExpertise = expertiseLevels.filter((l) => level >= l).length * 2;

  if (numExpertise === 0 || skillProficiencies.length === 0) return [];

  return shuffle([...skillProficiencies]).slice(0, Math.min(numExpertise, skillProficiencies.length));
}

/**
 * Generate fighting style for Fighter/Paladin/Ranger
 */
function generateFightingStyle(classId: string, level: number): string | null {
  const styleLevel = getFightingStyleLevel(classId);
  if (!styleLevel || level < styleLevel) return null;

  const styles = getFightingStylesForClass(classId as 'fighter' | 'paladin' | 'ranger');
  return styles.length > 0 ? pick(styles).id : null;
}

// =============================================================================
// Phase 3: Class-Specific Feature Generators
// =============================================================================

/**
 * Generate random Eldritch Invocations for Warlock
 * Levels: 2 (2 invocations), 5 (3), 7 (4), 9 (5)
 */
function generateEldritchInvocations(
  classId: string,
  warlockLevel: number,
  pactBoonId: string | null,
  knownCantrips: string[],
): string[] {
  if (classId !== 'warlock' || warlockLevel < 2) return [];

  const count = getInvocationCount(warlockLevel);
  const available = getAvailableInvocations(warlockLevel, pactBoonId, knownCantrips);

  return shuffle([...available]).slice(0, count).map((inv) => inv.id);
}

/**
 * Generate random Metamagic options for Sorcerer
 * Levels: 3 (2 options), 10 (3), 17 (4)
 */
function generateMetamagic(classId: string, sorcererLevel: number): string[] {
  if (classId !== 'sorcerer' || sorcererLevel < 3) return [];

  const count = getMetamagicCount(sorcererLevel);
  return shuffle([...METAMAGIC_OPTIONS]).slice(0, count).map((m) => m.id);
}

/**
 * Generate random Favored Enemies for Ranger
 * Levels: 1 (1 enemy), 6 (2), 14 (3)
 */
function generateFavoredEnemies(classId: string, rangerLevel: number): string[] {
  if (classId !== 'ranger' || rangerLevel < 1) return [];

  const count = getFavoredEnemyCount(rangerLevel);
  return shuffle([...FAVORED_ENEMIES]).slice(0, count).map((e) => e.id);
}

/**
 * Generate random Natural Explorer terrains for Ranger
 * Levels: 1 (1 terrain), 6 (2), 10 (3)
 */
function generateNaturalTerrains(classId: string, rangerLevel: number): string[] {
  if (classId !== 'ranger' || rangerLevel < 1) return [];

  const count = getNaturalExplorerCount(rangerLevel);
  return shuffle([...NATURAL_TERRAINS]).slice(0, count).map((t) => t.id);
}

/**
 * Generate random Pact Boon for Warlock
 * Warlocks choose a pact boon at level 3
 */
function generatePactBoon(classId: string, warlockLevel: number): string | null {
  if (classId !== 'warlock' || warlockLevel < 3) return null;
  return pick(PACT_BOONS).id;
}

// calculateModifier and calculateProficiencyBonus imported from @character-creator/core

/**
 * Generate attacks from equipment selections
 * Uses the same logic as CombatStep.generateSuggestedAttacks()
 */
function generateAttacks(
  characterClass: CharacterClass,
  equipmentSelections: Record<number, number>,
  abilityScores: AbilityScores,
  level: number,
): { name: string; bonus: string; damage: string; damageType: string }[] {
  const weaponItems: string[] = [];

  // Collect all weapon items from starting equipment
  characterClass.startingEquipment.forEach((item, index) => {
    if (typeof item === 'string') {
      weaponItems.push(item);
    } else if ('choose' in item && 'from' in item) {
      const selectedOptionIndex = equipmentSelections[index] ?? 0;
      const selectedOption = item.from[selectedOptionIndex];
      if (selectedOption) {
        weaponItems.push(...selectedOption);
      }
    }
  });

  // Find actual weapon data for each item
  const foundWeapons = weaponItems
    .map((itemId) => {
      const weapon = findWeaponByName(itemId);
      return weapon ? { itemId, weapon } : null;
    })
    .filter((w): w is NonNullable<typeof w> => w !== null);

  // Remove duplicates
  const uniqueWeapons = foundWeapons.filter(
    (w, i, arr) => arr.findIndex((x) => x.weapon.id === w.weapon.id) === i,
  );

  // Calculate modifiers
  const strMod = calculateModifier(abilityScores.strength);
  const dexMod = calculateModifier(abilityScores.dexterity);
  const profBonus = calculateProficiencyBonus(level);

  // Generate attacks (limit to 3 like CombatStep)
  return uniqueWeapons.slice(0, 3).map(({ weapon }) => {
    const { modifier } = getWeaponAbility(weapon, strMod, dexMod);
    const attackBonus = modifier + profBonus;
    const modStr = modifier >= 0 ? `+${modifier}` : `${modifier}`;

    return {
      name: weapon.name,
      bonus: formatAttackBonus(attackBonus),
      damage: weapon.damage === '0' ? 'Special' : `${weapon.damage}${modStr}`,
      damageType: weapon.damageType,
    };
  });
}

// applyAsiIncreases and applyMultipleAsiIncreases imported from @character-creator/core

// =============================================================================
// Multiclass Generation Functions
// =============================================================================

/**
 * Generate a random integer between min and max (inclusive)
 */
function randomInt(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

// meetsMulticlassPrerequisites imported from @character-creator/core

/**
 * Split total level between two classes.
 * Primary class gets 60-80% of levels, minimum 2 levels each.
 */
function splitLevels(totalLevel: number): [number, number] {
  // For level 3, only option is [2, 1]
  if (totalLevel === 3) return [2, 1];

  // Primary class gets 60-80% of levels
  const primaryMin = Math.max(2, Math.floor(totalLevel * 0.6));
  const primaryMax = Math.min(totalLevel - 1, Math.ceil(totalLevel * 0.8));
  const primaryLevel = randomInt(primaryMin, primaryMax);
  const secondaryLevel = totalLevel - primaryLevel;

  return [primaryLevel, secondaryLevel];
}

/**
 * Select a second class that the character can multiclass into
 */
function selectSecondClass(
  primaryClass: CharacterClass,
  allClasses: CharacterClass[],
  abilityScores: AbilityScores,
): CharacterClass | null {
  // Filter: different from primary, meets prereqs for BOTH classes
  // (You need to meet prereqs of your current class AND the new class to multiclass)
  const eligible = allClasses.filter(
    (c) =>
      c.id !== primaryClass.id &&
      meetsMulticlassPrerequisites(primaryClass, abilityScores) &&
      meetsMulticlassPrerequisites(c, abilityScores),
  );

  return eligible.length > 0 ? pick(eligible) : null;
}

/**
 * Create a ClassLevel entry for a class at a given level
 */
function createClassLevel(
  characterClass: CharacterClass,
  classLevel: number,
  skills: SkillName[],
  abilityScores: AbilityScores,
  rulesetId: string,
  raceId: string,
): {
  classId: string;
  level: number;
  skillProficiencyChoices: SkillName[];
  subclassId: string | null;
  fightingStyleId: string | null;
  expertiseSkills: SkillName[];
  landCircleTerrainId: string | null;
  pactBoonId: string | null;
  favoredEnemies: string[];
  naturalExplorerTerrains: string[];
  metamagicChoices: string[];
  eldritchInvocations: string[];
  asiChoices: Record<number, AsiChoice>;
} {
  const classId = characterClass.id;
  const hasSpellcasting = characterClass.spellcasting !== undefined;

  // Generate spells to get cantrips for invocation prereq checking
  const spellSelection = hasSpellcasting
    ? getRandomSpellSelection(classId, classLevel)
    : { cantrips: [], knownSpells: [] };

  // Generate pact boon first (needed for invocation prereqs)
  const pactBoonId = generatePactBoon(classId, classLevel);

  return {
    classId,
    level: classLevel,
    skillProficiencyChoices: skills,
    subclassId: generateSubclass(characterClass, classLevel),
    fightingStyleId: generateFightingStyle(classId, classLevel),
    expertiseSkills: generateExpertise(classId, classLevel, skills),
    landCircleTerrainId: null,
    pactBoonId,
    // Phase 3: Class-specific features
    favoredEnemies: generateFavoredEnemies(classId, classLevel),
    naturalExplorerTerrains: generateNaturalTerrains(classId, classLevel),
    metamagicChoices: generateMetamagic(classId, classLevel),
    eldritchInvocations: generateEldritchInvocations(
      classId,
      classLevel,
      pactBoonId,
      spellSelection.cantrips,
    ),
    asiChoices: generateAsiChoices(
      classId,
      classLevel,
      abilityScores,
      rulesetId,
      raceId,
      hasSpellcasting,
    ),
  };
}

/**
 * Generate a completely random character draft
 * @param rulesetId - The ruleset to use for filtering content (defaults to 'srd-5.1')
 * @param targetLevel - Optional specific level (1-10). If undefined, random level weighted toward 3-6.
 */
export function generateRandomCharacter(rulesetId: string = 'srd-5.1', targetLevel?: number): Partial<CharacterDraft> {
  // Generate character level (1-10, weighted toward mid-levels)
  const level = generateLevel(targetLevel);

  // Pick random race from the ruleset
  const races = getRacesForRuleset(rulesetId);
  const race = pick(races);
  const subrace = race.subraces && race.subraces.length > 0 ? pick(race.subraces) : null;

  // Pick random class from the ruleset
  const classes = getClassesForRuleset(rulesetId);
  const primaryClass = pick(classes);

  // Generate random ability scores (base scores before ASI)
  const baseAbilityScores = generateRandomAbilityScores();

  // Pick random background from the ruleset
  const backgrounds = getBackgroundsForRuleset(rulesetId);
  const background = pick(backgrounds);

  // Multiclass: 20% chance if level >= 3
  const shouldMulticlass = level >= 3 && Math.random() < 0.2;

  if (shouldMulticlass) {
    // Try to find an eligible second class
    const secondClass = selectSecondClass(primaryClass, classes, baseAbilityScores);

    if (secondClass) {
      // Generate multiclass character
      return generateMulticlassCharacter(
        rulesetId,
        primaryClass,
        secondClass,
        level,
        race,
        subrace,
        baseAbilityScores,
        background,
      );
    }
    // Fall through to single-class if no eligible second class
  }

  // Single-class character generation
  return generateSingleClassCharacter(
    rulesetId,
    primaryClass,
    level,
    race,
    subrace,
    baseAbilityScores,
    background,
  );
}

/**
 * Generate a single-class character
 */
function generateSingleClassCharacter(
  rulesetId: string,
  characterClass: CharacterClass,
  level: number,
  race: ReturnType<typeof getRacesForRuleset>[number],
  subrace: { id: string } | null,
  baseAbilityScores: AbilityScores,
  background: Background,
): Partial<CharacterDraft> {
  // Pick random skills for the class
  const availableSkills = characterClass.skillChoices.from;
  const requiredCount = characterClass.skillChoices.count;
  const shuffledSkills = [...availableSkills].sort(() => Math.random() - 0.5);
  const selectedSkills = shuffledSkills.slice(0, requiredCount) as SkillName[];

  // Determine if class has spellcasting
  const hasSpellcasting = characterClass.spellcasting !== undefined;

  // Generate ASI/Feat choices based on level
  const asiChoices = generateAsiChoices(
    characterClass.id,
    level,
    baseAbilityScores,
    rulesetId,
    race.id,
    hasSpellcasting,
  );

  // Apply ASI increases to get final ability scores
  const abilityScores = applyAsiIncreases(baseAbilityScores, asiChoices);

  // Generate random equipment selections for the class
  const equipmentSelections = getRandomEquipmentSelections(characterClass.id);

  // Generate random spell selections scaled to level (empty for non-casters)
  const spellSelection = getRandomSpellSelection(characterClass.id, level);

  // Generate random name for backstory
  const name = generateRandomName();

  // Generate personality traits from background
  const personalityTraits = [pick(background.suggestedCharacteristics.personalityTraits)];
  const ideals = [pick(background.suggestedCharacteristics.ideals)];
  const bonds = [pick(background.suggestedCharacteristics.bonds)];
  const flaws = [pick(background.suggestedCharacteristics.flaws)];

  // Generate subclass based on level and class requirements
  const subclassId = generateSubclass(characterClass, level);

  // Generate fighting style for martial classes
  const fightingStyleId = generateFightingStyle(characterClass.id, level);

  // Generate expertise for Rogue/Bard
  const expertiseSkills = generateExpertise(characterClass.id, level, selectedSkills);

  // Generate backstory using character details
  const backstory = generateBackstory(
    name,
    characterClass.name,
    background.name,
    ideals[0],
    bonds[0],
  );

  return {
    name,
    characterLevel: level,
    raceId: race.id,
    subraceId: subrace?.id ?? null,
    abilityBonusChoices: [],
    languageChoices: [],
    // Multiclass-aware class fields
    classLevels: [
      {
        classId: characterClass.id,
        level,
        skillProficiencyChoices: selectedSkills,
        subclassId,
        fightingStyleId,
        expertiseSkills,
        landCircleTerrainId: null,
        pactBoonId: null,
        favoredEnemies: [],
        naturalExplorerTerrains: [],
        metamagicChoices: [],
        eldritchInvocations: [],
        asiChoices,
      },
    ],
    classId: characterClass.id,
    skillProficiencyChoices: selectedSkills,
    confirmedCommoner: false,
    abilityScoreMethod: 'manual',
    abilityScores,
    backgroundId: background.id,
    alignment: generateAlignment(),
    personalityTraits,
    ideals,
    bonds,
    flaws,
    backstory,
    appearance: generateAppearance(),
    currency: generateCurrency(background),
    // Note: hitPoints are calculated from class, level, and CON modifier, not stored in draft
    // Equipment: random selection from class starting equipment choices
    equipmentSelections,
    // Spells: random valid spells for class (empty arrays for non-casters)
    selectedCantrips: spellSelection.cantrips,
    knownSpells: spellSelection.knownSpells,
    // Don't auto-prepare spells - let user decide what to prepare
    preparedSpells: [],
    // Attacks: generated from starting weapons
    attacks: generateAttacks(characterClass, equipmentSelections, abilityScores, level),
  };
}

/**
 * Generate a multiclass character with two classes
 */
function generateMulticlassCharacter(
  rulesetId: string,
  primaryClass: CharacterClass,
  secondaryClass: CharacterClass,
  totalLevel: number,
  race: ReturnType<typeof getRacesForRuleset>[number],
  subrace: { id: string } | null,
  baseAbilityScores: AbilityScores,
  background: Background,
): Partial<CharacterDraft> {
  // Split levels between primary and secondary class
  const [primaryLevel, secondaryLevel] = splitLevels(totalLevel);

  // Pick random skills for primary class (full skill selection)
  const primaryAvailableSkills = primaryClass.skillChoices.from;
  const primaryRequiredCount = primaryClass.skillChoices.count;
  const primaryShuffledSkills = [...primaryAvailableSkills].sort(() => Math.random() - 0.5);
  const primarySkills = primaryShuffledSkills.slice(0, primaryRequiredCount) as SkillName[];

  // Secondary class gets limited skill options per multiclass rules
  // Only Rogue gets extra skill proficiencies when multiclassing (1 skill from Rogue list)
  let secondarySkills: SkillName[] = [];
  if (secondaryClass.id === 'rogue') {
    const rogueSkillChoices = secondaryClass.skillChoices.from.filter(
      (s) => !primarySkills.includes(s as SkillName),
    );
    if (rogueSkillChoices.length > 0) {
      secondarySkills = [pick(rogueSkillChoices)] as SkillName[];
    }
  }

  // Generate ASI choices for each class based on CLASS levels (not character level)
  const primaryHasSpellcasting = primaryClass.spellcasting !== undefined;
  const secondaryHasSpellcasting = secondaryClass.spellcasting !== undefined;

  const primaryAsiChoices = generateAsiChoices(
    primaryClass.id,
    primaryLevel,
    baseAbilityScores,
    rulesetId,
    race.id,
    primaryHasSpellcasting,
  );

  // For secondary class ASIs, we need to apply primary ASIs first to get updated scores
  const scoresAfterPrimaryAsi = applyAsiIncreases(baseAbilityScores, primaryAsiChoices);

  const secondaryAsiChoices = generateAsiChoices(
    secondaryClass.id,
    secondaryLevel,
    scoresAfterPrimaryAsi,
    rulesetId,
    race.id,
    secondaryHasSpellcasting,
  );

  // Apply all ASI increases to get final ability scores
  const abilityScores = applyMultipleAsiIncreases(baseAbilityScores, [primaryAsiChoices, secondaryAsiChoices]);

  // Create ClassLevel entries for both classes
  const primaryClassLevel = createClassLevel(
    primaryClass,
    primaryLevel,
    primarySkills,
    baseAbilityScores,
    rulesetId,
    race.id,
  );
  // Override asiChoices since createClassLevel generates them fresh
  primaryClassLevel.asiChoices = primaryAsiChoices;

  const secondaryClassLevel = createClassLevel(
    secondaryClass,
    secondaryLevel,
    secondarySkills,
    scoresAfterPrimaryAsi,
    rulesetId,
    race.id,
  );
  // Override asiChoices since createClassLevel generates them fresh
  secondaryClassLevel.asiChoices = secondaryAsiChoices;

  // Generate random equipment selections for primary class (you only get starting equipment once)
  const equipmentSelections = getRandomEquipmentSelections(primaryClass.id);

  // Generate random spell selections for primary class at its level
  // (Multiclass spellcasting is complex - we simplify by using primary class spells)
  const spellSelection = getRandomSpellSelection(primaryClass.id, primaryLevel);

  // Generate random name for backstory
  const name = generateRandomName();

  // Generate personality traits from background
  const personalityTraits = [pick(background.suggestedCharacteristics.personalityTraits)];
  const ideals = [pick(background.suggestedCharacteristics.ideals)];
  const bonds = [pick(background.suggestedCharacteristics.bonds)];
  const flaws = [pick(background.suggestedCharacteristics.flaws)];

  // Generate backstory mentioning both classes
  const backstory = generateMulticlassBackstory(
    name,
    primaryClass.name,
    secondaryClass.name,
    background.name,
    ideals[0],
    bonds[0],
  );

  return {
    name,
    characterLevel: totalLevel,
    raceId: race.id,
    subraceId: subrace?.id ?? null,
    abilityBonusChoices: [],
    languageChoices: [],
    // Multiclass: two ClassLevel entries
    classLevels: [primaryClassLevel, secondaryClassLevel],
    // Legacy field: primary class
    classId: primaryClass.id,
    skillProficiencyChoices: primarySkills,
    confirmedCommoner: false,
    abilityScoreMethod: 'manual',
    abilityScores,
    backgroundId: background.id,
    alignment: generateAlignment(),
    personalityTraits,
    ideals,
    bonds,
    flaws,
    backstory,
    appearance: generateAppearance(),
    currency: generateCurrency(background),
    // Equipment: from primary class only
    equipmentSelections,
    // Spells: from primary class only (simplified)
    selectedCantrips: spellSelection.cantrips,
    knownSpells: spellSelection.knownSpells,
    preparedSpells: [],
    // Attacks: generated from starting weapons (primary class)
    attacks: generateAttacks(primaryClass, equipmentSelections, abilityScores, totalLevel),
  };
}

/**
 * Generate a backstory for a multiclass character
 */
function generateMulticlassBackstory(
  name: string,
  primaryClassName: string,
  secondaryClassName: string,
  backgroundName: string,
  ideal: string,
  bond: string,
): string {
  const multiclassOrigins = [
    `began their journey as a ${primaryClassName.toLowerCase()} before the path of the ${secondaryClassName.toLowerCase()} called to them`,
    `trained first in the ways of the ${primaryClassName.toLowerCase()}, but destiny led them to also embrace the ${secondaryClassName.toLowerCase()}`,
    `found that the skills of both ${primaryClassName.toLowerCase()} and ${secondaryClassName.toLowerCase()} were needed to face the challenges ahead`,
    `combines the disciplines of ${primaryClassName.toLowerCase()} and ${secondaryClassName.toLowerCase()} in a unique way`,
  ];

  const motivation = pick(MOTIVATION_PHRASES);
  const idealShort = ideal.split('.')[0];
  const bondShort = bond.split('.')[0];

  return `${name} ${pick(multiclassOrigins)}. Their time as a ${backgroundName.toLowerCase()} shaped who they are today. When asked to describe their ideal, they say "${idealShort}. ${bondShort}." ${motivation}.`;
}

/**
 * Character template presets
 */
export interface CharacterTemplate {
  id: string;
  name: string;
  description: string;
  draft: Partial<CharacterDraft>;
}

export const CHARACTER_TEMPLATES: CharacterTemplate[] = [
  {
    id: 'human-fighter',
    name: 'Aldric, Human Fighter',
    description: 'A versatile warrior ready for battle',
    draft: {
      name: 'Aldric',
      characterLevel: 1,
      raceId: 'human',
      subraceId: null,
      // Fighter gets Martial Archetype at level 3
      classLevels: [{ classId: 'fighter', level: 1, skillProficiencyChoices: ['athletics', 'perception'] as SkillName[], subclassId: null, fightingStyleId: null, expertiseSkills: [], landCircleTerrainId: null, pactBoonId: null, favoredEnemies: [], naturalExplorerTerrains: [], metamagicChoices: [], eldritchInvocations: [], asiChoices: {} }],
      classId: 'fighter',
      skillProficiencyChoices: ['athletics', 'perception'] as SkillName[],
      confirmedCommoner: false,
      abilityScoreMethod: 'standard-array',
      abilityScores: {
        strength: 15,
        dexterity: 13,
        constitution: 14,
        intelligence: 10,
        wisdom: 12,
        charisma: 8,
      },
      backgroundId: 'acolyte',
      // Equipment: chain mail, longsword + shield, 2 handaxes, dungeoneer's pack
      equipmentSelections: {
        0: 0, // Chain mail (vs leather + longbow)
        1: 0, // Martial weapon + shield (vs two martial weapons)
        2: 1, // 2 handaxes (vs light crossbow + bolts)
        3: 0, // Dungeoneer's pack (vs explorer's pack)
      },
    },
  },
  {
    id: 'elf-wizard',
    name: 'Elyndra, High Elf Wizard',
    description: 'An arcane scholar seeking knowledge',
    draft: {
      name: 'Elyndra',
      characterLevel: 1,
      raceId: 'elf',
      subraceId: 'high-elf',
      // Wizard gets Arcane Tradition at level 2
      classLevels: [{ classId: 'wizard', level: 1, skillProficiencyChoices: ['arcana', 'history'] as SkillName[], subclassId: null, fightingStyleId: null, expertiseSkills: [], landCircleTerrainId: null, pactBoonId: null, favoredEnemies: [], naturalExplorerTerrains: [], metamagicChoices: [], eldritchInvocations: [], asiChoices: {} }],
      classId: 'wizard',
      skillProficiencyChoices: ['arcana', 'history'] as SkillName[],
      confirmedCommoner: false,
      abilityScoreMethod: 'standard-array',
      abilityScores: {
        strength: 8,
        dexterity: 14,
        constitution: 12,
        intelligence: 15,
        wisdom: 13,
        charisma: 10,
      },
      backgroundId: 'acolyte',
      // Wizard cantrips: fire bolt (damage), prestidigitation (utility), mage hand (utility)
      selectedCantrips: ['fire-bolt', 'prestidigitation', 'mage-hand'],
      // Wizard spellbook (6 spells at level 1)
      knownSpells: ['magic-missile', 'shield', 'mage-armor', 'detect-magic', 'find-familiar', 'sleep'],
      // Equipment: quarterstaff, arcane focus, scholar's pack (+ spellbook is fixed)
      equipmentSelections: {
        0: 0, // Quarterstaff (vs dagger)
        1: 1, // Arcane focus (vs component pouch)
        2: 0, // Scholar's pack (vs explorer's pack)
      },
    },
  },
  {
    id: 'dwarf-cleric',
    name: 'Durin, Dwarf Life Cleric',
    description: 'A stalwart healer devoted to the divine',
    draft: {
      name: 'Durin',
      characterLevel: 1,
      raceId: 'dwarf',
      subraceId: 'hill-dwarf',
      // Cleric gets Divine Domain at level 1
      classLevels: [{ classId: 'cleric', level: 1, skillProficiencyChoices: ['insight', 'medicine'] as SkillName[], subclassId: 'life-domain', fightingStyleId: null, expertiseSkills: [], landCircleTerrainId: null, pactBoonId: null, favoredEnemies: [], naturalExplorerTerrains: [], metamagicChoices: [], eldritchInvocations: [], asiChoices: {} }],
      classId: 'cleric',
      skillProficiencyChoices: ['insight', 'medicine'] as SkillName[],
      confirmedCommoner: false,
      abilityScoreMethod: 'standard-array',
      abilityScores: {
        strength: 13,
        dexterity: 8,
        constitution: 14,
        intelligence: 10,
        wisdom: 15,
        charisma: 12,
      },
      backgroundId: 'acolyte',
      // Cleric cantrips: sacred flame (damage), guidance (utility), spare the dying (utility)
      selectedCantrips: ['sacred-flame', 'guidance', 'spare-the-dying'],
      // Clerics prepare spells from their full list (Wis mod + cleric level)
      // Suggested prepared spells for a Life Cleric: healing word, cure wounds, bless, shield of faith
      knownSpells: ['cure-wounds', 'healing-word', 'bless', 'shield-of-faith'],
      // Equipment: warhammer (dwarf proficiency), chain mail (Life = heavy armor), crossbow, priest's pack
      equipmentSelections: {
        0: 1, // Warhammer (vs mace) - dwarves have warhammer proficiency
        1: 2, // Chain mail (vs scale-mail, leather) - Life domain grants heavy armor
        2: 0, // Light crossbow + bolts (vs any simple)
        3: 0, // Priest's pack (vs explorer's pack)
      },
    },
  },
  {
    id: 'halfling-rogue',
    name: 'Finn, Halfling Rogue',
    description: 'A nimble trickster with quick fingers',
    draft: {
      name: 'Finn',
      characterLevel: 1,
      raceId: 'halfling',
      subraceId: 'lightfoot',
      // Rogue gets Roguish Archetype at level 3
      classLevels: [{ classId: 'rogue', level: 1, skillProficiencyChoices: ['stealth', 'sleight-of-hand', 'acrobatics', 'perception'] as SkillName[], subclassId: null, fightingStyleId: null, expertiseSkills: [], landCircleTerrainId: null, pactBoonId: null, favoredEnemies: [], naturalExplorerTerrains: [], metamagicChoices: [], eldritchInvocations: [], asiChoices: {} }],
      classId: 'rogue',
      skillProficiencyChoices: ['stealth', 'sleight-of-hand', 'acrobatics', 'perception'] as SkillName[],
      confirmedCommoner: false,
      abilityScoreMethod: 'standard-array',
      abilityScores: {
        strength: 8,
        dexterity: 15,
        constitution: 12,
        intelligence: 13,
        wisdom: 10,
        charisma: 14,
      },
      backgroundId: 'acolyte',
      // Equipment: rapier, shortbow + arrows, burglar's pack (+ leather, 2 daggers, thieves' tools fixed)
      equipmentSelections: {
        0: 0, // Rapier (vs shortsword) - finesse weapon
        1: 0, // Shortbow + quiver + arrows (vs shortsword) - ranged option
        2: 0, // Burglar's pack (vs dungeoneer's, explorer's) - thematic
      },
    },
  },
];
